import { Component, ChangeDetectionStrategy, inject, signal, effect, untracked } from "@angular/core";
import { ReactiveFormsModule, FormControl, FormGroup, FormArray, Validators } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateService, TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { FloatLabelModule } from "primeng/floatlabel";
import { TextareaModule } from "primeng/textarea";
import { ChipModule } from "primeng/chip";

import { UnderReviewQuote, SalesSupportPerson } from "src/api-service";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { selectUserContext } from "src/app/store/user/user.selectors";
import { selectSalesSupportPersons } from "src/app/store/application/data/sales-support-person/sales-support-persons/sales-support-persons.selectors";
import { strictEmailValidator } from "src/app/shared/validators/validations";
import { selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";

@Component({
    selector: "app-review-order-dialog",
    templateUrl: "./review-order-dialog.component.html",
    styleUrls: ["./review-order-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        CoreErrorsComponent,
        ButtonModule,
        ToggleSwitchModule,
        SelectModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        FloatLabelModule,
        TextareaModule,
        ChipModule,
    ],
})
export class ReviewOrderDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quote = toSignal(this.store.select(selectQuoteState));
    private readonly selectedVersion = toSignal(this.store.select(selectVersion));
    private readonly user = toSignal(this.store.select(selectUserContext));
    public readonly salesSupportPersons = toSignal(this.store.select(selectSalesSupportPersons), { initialValue: [] });

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly sendMail = signal(false);
    public readonly showCommentField = signal(false);
    public validCcEmails: string[] = [];
    public ccEmailControl = new FormControl("", strictEmailValidator());
    private readonly initialized = signal(false);

    // =========================================================================
    // FORM
    // =========================================================================
    public readonly form = new FormGroup({
        salesSupportPerson: new FormControl<SalesSupportPerson | null>(null, Validators.required),
        recipientEmail: new FormControl<string | null>(null),
        ccEmails: new FormArray<FormControl<string>>([]),
        message: new FormControl({ value: "", disabled: true }),
        showCommentField: new FormControl(false),
        comment: new FormControl(""),
    });

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly initFormEffect = effect(() => {
        const quote = this.quote();
        const user = this.user();
        const salesSupportPersons = this.salesSupportPersons();

        if (quote && user && salesSupportPersons.length && !untracked(() => this.initialized())) {
            this.initialized.set(true);

            const salesSupportPerson = salesSupportPersons.find(x => x.email === user.email) ?? null;

            this.form.patchValue({
                salesSupportPerson,
                recipientEmail: quote.salesInfo?.responsibleSalesPerson?.email ?? null,
                showCommentField: false,
                comment: "",
            });

            this.updateMailMessage();
        }
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public onSendMailChange(value: boolean): void {
        this.sendMail.set(value);

        if (value) {
            this.form.get("recipientEmail")?.setValidators([Validators.required, strictEmailValidator()]);
            this.form.get("message")?.setValidators(Validators.required);
        } else {
            this.form.get("recipientEmail")?.clearValidators();
            this.form.get("message")?.clearValidators();
        }
        this.form.get("recipientEmail")?.updateValueAndValidity();
        this.form.get("message")?.updateValueAndValidity();
    }

    public onSalesSupportPersonChange(): void {
        this.updateMailMessage();
    }

    public onShowCommentFieldChange(value: boolean): void {
        this.showCommentField.set(value);
    }

    public addCcRecipient(): void {
        const email = this.ccEmailControl.value?.trim() ?? "";

        if (!email) {
            return;
        }

        // Mark as touched to show validation errors
        this.ccEmailControl.markAsTouched();

        if (this.ccEmailControl.invalid) {
            return;
        }

        // Check for duplicates
        if (this.validCcEmails.includes(email)) {
            this.ccEmailControl.reset();
            return;
        }

        // Add valid email
        this.validCcEmails.push(email);
        this.ccEmailControl.reset();
    }

    public removeCcRecipient(email: string): void {
        this.validCcEmails = this.validCcEmails.filter(e => e !== email);
    }

    public onCancel(): void {
        this.ref.close();
    }

    public onConfirm(): void {
        if (this.form.invalid) return;

        const quote = this.quote();
        const version = this.selectedVersion();
        const user = this.user();

        if (!quote || !version || !user) return;

        const request: UnderReviewQuote = {
            sendMail: this.sendMail(),
            salesSupportPersonId: this.form.controls.salesSupportPerson.value?.id,
            subject: this.translateService.instant("REVIEW_ORDER.MAIL_SUBJECT", {
                quoteId: quote.id,
                salesSupportName: this.form.controls.salesSupportPerson.value?.name,
                companyId: user.selectedCompany?.id,
            }),
            versionId: version.id,
            recipientEmail: this.form.controls.recipientEmail.value ?? "",
            ccEmails: this.validCcEmails,
            message: this.buildEmailBody(
                this.form.controls.message.value ?? "",
                this.form.controls.comment.value ?? "",
                user.email,
            ),
            comment: this.form.controls.comment.value ?? "",
        };

        this.store.dispatch(
            QuoteActions.updateToUnderReview({
                quoteId: quote.id,
                request,
                dialogKey: 'TO_UNDER_REVIEW',
            }),
        );
        this.ref.close();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private updateMailMessage(): void {
        const salesSupportPerson = this.form.controls.salesSupportPerson.value;

        if (!salesSupportPerson) {
            this.form.get("recipientEmail")?.clearValidators();
            this.form.get("message")?.clearValidators();
            this.sendMail.set(false);
            return;
        }

        this.form.controls.message.setValue(
            this.translateService.instant("REVIEW_ORDER.MAIL_MESSAGE", {
                quoteId: this.quote()?.id,
                salesSupportName: salesSupportPerson.name,
            }),
        );
    }

    private buildEmailBody(message: string, comment: string, email: string): string {
        let body = message ?? "";
        if (comment?.length > 0) {
            body += "\n" + this.translateService.instant("COMMENT") + ": " + comment;
        }
        body += "\n\n" + this.translateService.instant("MAIL.SENT_BY") + ": " + email;
        body += "\n" + this.translateService.instant("MAIL.NO_REPLY_INFO");
        return body;
    }
}
